import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
  Input,
  model,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as uuid from 'uuid';
import { InputTypes } from '@core/enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { LabelOptions } from '@core/classes/options/label-options';

export class InputOptions {
  id?: string = uuid.v4();
  label?: LabelOptions;
  placeholder?: string;
  name?: string;
  type?: InputTypes = InputTypes.text;
  mode?: string;
  value?: string;
  pattern?: RegExp;
  inputClass?: string;
  onKeyDown?: (event: KeyboardEvent, value: string) => void;
  onKeyUp?: (event: KeyboardEvent, value: string) => void;
  onInput?: (el: HTMLInputElement, event?: Event) => any;
  onBlur?: (value: string) => void;
  resizeOnInput?: boolean;
  maxSize?: number;
  initialValue?: any;
  formControl?: FormControl<any>;
  formGroup?: FormGroup;
  formControlName?: string;
  required?: boolean | true;
  autocomplete?: 'off'| 'on';
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number | string;

  constructor(args?: InputOptions) {
    this.id = args?.id || uuid.v4();
    this.label = args?.label;
    this.placeholder = args?.placeholder || '';
    this.name = args?.name || '';
    this.type = args?.type || InputTypes.text;
    this.mode = args?.mode || '';
    this.value = args?.value || '';
    this.pattern = args?.pattern;
    this.inputClass = args?.inputClass || '';
    this.onKeyDown = args?.onKeyDown;
    this.onKeyUp = args?.onKeyUp;
    this.onInput = args?.onInput;
    this.onBlur = args?.onBlur;
    this.resizeOnInput = args?.resizeOnInput || false;
    this.maxSize = args?.maxSize;
    this.initialValue = args?.initialValue;
    this.formControl = args?.formControl;
    this.formGroup = args?.formGroup;
    this.formControlName = args?.formControlName;
    this.required = args?.required !== undefined ? args?.required : false;
    this.autocomplete = args?.autocomplete || 'on';
    this.disabled = args?.disabled !== undefined ? args?.disabled : false;
    this.min = args?.min;
    this.max = args?.max;
    this.step = args?.step;
  }
}

@UntilDestroy()
@Component({
  selector: 'ncp-input',
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
})
export class InputComponent implements OnInit, AfterViewInit {
  @ViewChild('el') public el?: ElementRef;
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  protected translations = this.translate.instant('input');
  protected invalidMessage: string = '';

  options = model<InputOptions>(new InputOptions({}));

  // options = input(new InputOptions(), {
  //   transform: (value: InputOptions) => {
  //     if (!value) {
  //       return new InputOptions({});
  //     }
  //
  //     if (value.inputClass) {
  //       value.inputClass = this.options().inputClass + ' ' + value.inputClass;
  //     }
  //
  //     if (value.initialValue) {
  //       if (value.formGroup && value.formControlName) {
  //         value.formGroup.controls[value.formControlName].setValue(value.initialValue);
  //       } else if (value.formControlName) {
  //         value.formControl?.setValue(value.initialValue);
  //       }
  //     }
  //
  //     if (value.resizeOnInput) {
  //       this.el?.nativeElement.setAttribute('size', '' + this.el?.nativeElement.value.length);
  //     }
  //
  //     if (value.maxSize) {
  //       this.el?.nativeElement.setAttribute('size', '' + value.maxSize!);
  //     }
  //
  //     return value;
  //   },
  // });

  ngOnInit() {
    this.reset();

    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('input');
    });
  }

  detectChanges() {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit() {
    if (this.options().resizeOnInput) {
      this.el?.nativeElement.setAttribute('size', '' + this.el?.nativeElement.value.length);
    }
    if (this.options().maxSize) {
      this.el?.nativeElement.setAttribute('size', this.options().maxSize!);
    }
  }

  onKeyDown(event: KeyboardEvent, value: string) {
    if (this.options()?.onKeyDown) {
      this.options().onKeyDown?.(event, value);
    }
  }

  onKeyUp(event: KeyboardEvent, value: string, el?: HTMLInputElement) {
    if(this.options()?.min || this.options()?.max) {
      if (el && el.value != "") {
        if (this.options()?.min && (parseInt(el.value) < parseInt(el.min))) {
          el.value = el.min;
          if(this.options().formControl) {
            (this.options().formControl as FormControl).setValue(el.min);
          }
        }
        if (this.options()?.max && (parseInt(el.value) > parseInt(el.max))) {
          el.value = el.max;
          if(this.options().formControl) {
            (this.options().formControl as FormControl).setValue(el.max);
          }
        }
      }

      if(el && el.value === "") {
        el.value = el.value.slice(0, -1);
      }
    }

    console.log(el?.value);
    if (this.options()?.onKeyUp) {
      this.options()?.onKeyUp?.(event, value);
    }
  }

  onInput(input: HTMLInputElement, event?: Event) {
    if (this.options()?.onInput) {
      this.options().value = this.options().onInput?.(input, event);
      input.value = this.options().value || '';
    }
  }

  onBlur(value: string) {
    if (this.options()?.onBlur) {
      this.options()?.onBlur?.(value);
    }
  }

  enable() {
    if(this.options().formControl) {
      this.options().formControl?.enable();
    }
  }

  disable() {
    if(this.options().formControl) {
      this.options().formControl?.disable();
    }
  }

  reset() {
    if (this.options().initialValue) {
      if (this.options().formGroup && this.options().formControlName) {
        this.options().formGroup?.controls[this.options()?.formControlName!].setValue(
          this.options().initialValue
        );
      } else if (this.options().formControlName) {
        this.options().formControl?.setValue(this.options().initialValue);
      }
    }

    if(this.options().disabled) {
      this.disable();
    }

    if (this.options().resizeOnInput) {
      this.el?.nativeElement.setAttribute('size', '' + this.el?.nativeElement.value.length);
    }

    if (this.options().maxSize) {
      this.el?.nativeElement.setAttribute('size', '' + this.options().maxSize!);
    }
  }

  protected readonly oninput = oninput;
}
